export const WordCompletionStatus = {
  UPCOMING: "UPCOMING",
  CURRENT: "CURRENT",
  CORRECT: "CORRECT",
  INCORRECT: "INCORRECT",
  PARTIALLY_CORRECT: "PARTIALLY_CORRECT",
  SKIPPED: "SKIPPED",
};

export type WordCompletionStatusType =
  | typeof WordCompletionStatus.UPCOMING
  | typeof WordCompletionStatus.CURRENT
  | typeof WordCompletionStatus.CORRECT
  | typeof WordCompletionStatus.INCORRECT
  | typeof WordCompletionStatus.PARTIALLY_CORRECT
  | typeof WordCompletionStatus.SKIPPED;

export interface SpeedState {
  isOnIntro: boolean;
  numWrongAnswers: number;
  numSkips: number;
  maxDuration: number;
  maxStreak: number;
  timeRemaining: number;
  isPaused: boolean;
  completedWordIndex: number;
  completedWordStatuses: Array<WordCompletionStatusType>;
}

export const INITIAL_STATE: SpeedState = {
  isOnIntro: true,
  numWrongAnswers: 0,
  numSkips: 0,
  maxDuration: -1,
  maxStreak: 0,
  timeRemaining: -1,
  isPaused: false,
  completedWordIndex: 0,
  completedWordStatuses: [],
};

export const SpeedAction = {
  INTRO_CLOSED: "INTRO_CLOSED",
  ROUND_STARTED: "ROUND_STARTED",
  TIME_ELAPSED: "TIME_ELAPSED",
  NEW_MAX_STREAK: "NEW_MAX_STREAK",
  PAUSE_PRESSED: "PAUSE_PRESSED",
  RESUME_PRESSED: "RESUME_PRESSED",
  SKIPPED: "SKIPPED",
  ANSWERED_CORRECTLY: "ANSWERED_CORRECTLY",
  ANSWERED_PARTIALLY_CORRECT: "ANSWERED_PARTIALLY_CORRECT",
  ANSWERED_INCORRECTLY: "ANSWERED_INCORRECTLY",
  TIMER_EXPIRED: "TIMER_EXPIRED",
  ROUND_COMPLETED: "ROUND_COMPLETED",
} as const;

export type SpeedAction =
  | { type: typeof SpeedAction.INTRO_CLOSED }
  | {
      type: typeof SpeedAction.ROUND_STARTED;
      payload: { roundDuration: number };
    }
  | { type: typeof SpeedAction.TIME_ELAPSED; payload: { time: number } }
  | { type: typeof SpeedAction.NEW_MAX_STREAK; payload: { newStreak: number } }
  | { type: typeof SpeedAction.PAUSE_PRESSED }
  | { type: typeof SpeedAction.RESUME_PRESSED }
  | { type: typeof SpeedAction.SKIPPED }
  | { type: typeof SpeedAction.ANSWERED_CORRECTLY }
  | { type: typeof SpeedAction.ANSWERED_PARTIALLY_CORRECT }
  | { type: typeof SpeedAction.ANSWERED_INCORRECTLY }
  | { type: typeof SpeedAction.TIMER_EXPIRED }
  | { type: typeof SpeedAction.ROUND_COMPLETED };

export function speedReducer(state: SpeedState, action: SpeedAction) {
  switch (action.type) {
    case SpeedAction.INTRO_CLOSED:
      return {
        ...state,
        isOnIntro: false,
      };
    case SpeedAction.ROUND_STARTED:
      return {
        ...state,
        maxDuration: action.payload.roundDuration,
        timeRemaining:
          state.timeRemaining == -1
            ? action.payload.roundDuration
            : state.timeRemaining,
      };
    case SpeedAction.TIME_ELAPSED:
      return {
        ...state,
        timeRemaining: action.payload.time,
      };
    case SpeedAction.NEW_MAX_STREAK:
      return {
        ...state,
        maxStreak: action.payload.newStreak,
      };
    case SpeedAction.PAUSE_PRESSED:
      return {
        ...state,
        isPaused: true,
      };
    case SpeedAction.RESUME_PRESSED:
      return {
        ...state,
        isPaused: false,
      };
    case SpeedAction.ANSWERED_CORRECTLY:
      return {
        ...state,
        completedWordIndex: state.completedWordIndex + 1,
        completedWordStatuses: [
          ...state.completedWordStatuses,
          WordCompletionStatus.CORRECT,
        ],
      };
    case SpeedAction.SKIPPED:
      return {
        ...state,
        completedWordIndex: state.completedWordIndex + 1,
        numSkips: state.numSkips + 1,
        completedWordStatuses: [
          ...state.completedWordStatuses,
          WordCompletionStatus.SKIPPED,
        ],
      };
    case SpeedAction.ANSWERED_INCORRECTLY:
      return {
        ...state,
        numWrongAnswers: state.numWrongAnswers + 1,
        completedWordIndex: state.completedWordIndex + 1,
        completedWordStatuses: [
          ...state.completedWordStatuses,
          WordCompletionStatus.INCORRECT,
        ],
      };
    case SpeedAction.ANSWERED_PARTIALLY_CORRECT:
      return {
        ...state,
        completedWordIndex: state.completedWordIndex + 1,
        completedWordStatuses: [
          ...state.completedWordStatuses,
          WordCompletionStatus.PARTIALLY_CORRECT,
        ],
      };
    case SpeedAction.TIMER_EXPIRED:
      return {
        ...state,
        timeRemaining: 0,
      };

    case SpeedAction.ROUND_COMPLETED:
      return INITIAL_STATE;
    default:
      return state;
  }
}
